import React, { useState, useRef, useEffect } from 'react';
import { FontFamily, AVAILABLE_FONTS } from '../../_types/invitation-design';

interface FontSelectorProps {
  value: string;
  onChange: (fontFamily: string) => void;
  label: string;
  placeholder?: string;
  isTemplateVariable?: boolean; // indicates if value is a template variable
  themeFonts?: {
    primary?: string;
    secondary?: string;
  };
}

const FontSelector: React.FC<FontSelectorProps> = ({
  value,
  onChange,
  label,
  placeholder = "Select a font...",
  isTemplateVariable = false,
  themeFonts = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fonts = Object.values(AVAILABLE_FONTS);
  
  // Filter fonts based on search term
  const filteredFonts = fonts.filter(font =>
    font.family.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group fonts by category
  const fontsByCategory = filteredFonts.reduce((acc, font) => {
    if (!acc[font.category]) {
      acc[font.category] = [];
    }
    acc[font.category].push(font);
    return acc;
  }, {} as Record<string, FontFamily[]>);

  // Check if we're showing a theme font
  const isThemePrimary = value === '{{template.theme.fontFamily}}';
  const isThemeSecondary = value === '{{template.theme.secondaryFontFamily}}';
  
  // For non-template values, try to find the font
  const selectedFont = !isThemePrimary && !isThemeSecondary 
    ? fonts.find(font => font.family === value)
    : null;

  const handleFontSelect = (fontFamily: string) => {
    onChange(fontFamily);
    setIsOpen(false);
    setSearchTerm('');
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'serif': return 'Serif Fonts';
      case 'sans-serif': return 'Sans-Serif Fonts';
      case 'script': return 'Script & Handwriting';
      case 'display': return 'Display & Decorative';
      case 'condensed': return 'Narrow & Condensed';
      default: return category;
    }
  };

  const getFontPreviewText = (category: string) => {
    switch (category) {
      case 'script': return 'Beautiful Wedding';
      case 'display': return 'CELEBRATION';
      case 'condensed': return 'ELEGANT DESIGN';
      default: return 'Sample Text';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}
      </label>
      
      {/* Selected Font Display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 bg-white text-left flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          {isThemePrimary ? (
            <>
              <span 
                className="text-base"
                style={{ fontFamily: themeFonts.primary || 'serif' }}
              >
                Aa
              </span>
              <div>
                <div className="text-sm font-medium text-gray-900">Theme Primary</div>
                <div className="text-xs text-gray-500">{themeFonts.primary || 'Default theme font'}</div>
              </div>
            </>
          ) : isThemeSecondary ? (
            <>
              <span 
                className="text-base"
                style={{ fontFamily: themeFonts.secondary || 'serif' }}
              >
                Aa
              </span>
              <div>
                <div className="text-sm font-medium text-gray-900">Theme Secondary</div>
                <div className="text-xs text-gray-500">{themeFonts.secondary || 'Default theme font'}</div>
              </div>
            </>
          ) : selectedFont ? (
            <>
              <span 
                className="text-base"
                style={{ fontFamily: selectedFont.family }}
              >
                Aa
              </span>
              <div>
                <div className="text-sm font-medium text-gray-900">{selectedFont.family}</div>
                <div className="text-xs text-gray-500 capitalize">{selectedFont.category}</div>
              </div>
            </>
          ) : (
            <span className="text-sm text-gray-500">{placeholder}</span>
          )}
        </div>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-80 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search fonts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
            />
          </div>

          {/* Font List */}
          <div className="overflow-y-auto max-h-64">
            {/* Theme Fonts Section */}
            {(
              <div>
                <div className="px-2 py-1 bg-gray-50 border-b border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Theme Fonts
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => handleFontSelect('{{template.theme.fontFamily}}')}
                  className={`w-full px-2 py-2 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors ${
                    isThemePrimary ? 'bg-green-50 border-green-200' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span 
                      className="text-lg text-gray-700"
                      style={{ fontFamily: themeFonts.primary || 'serif' }}
                    >
                      Aa
                    </span>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">Theme Primary</div>
                      <div className="text-xs text-gray-600">{themeFonts.primary || 'Default theme font'}</div>
                    </div>
                    {isThemePrimary && (
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleFontSelect('{{template.theme.secondaryFontFamily}}')}
                  className={`w-full px-2 py-2 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors ${
                    isThemeSecondary ? 'bg-green-50 border-green-200' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span 
                      className="text-lg text-gray-700"
                      style={{ fontFamily: themeFonts.secondary || themeFonts.primary || 'serif' }}
                    >
                      Aa
                    </span>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">Theme Secondary</div>
                      <div className="text-xs text-gray-600">{themeFonts.secondary || themeFonts.primary || 'Default theme font'}</div>
                    </div>
                    {isThemeSecondary && (
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              </div>
            )}
            
            {Object.entries(fontsByCategory).map(([category, categoryFonts]) => (
              <div key={category}>
                <div className="px-2 py-1 bg-gray-50 border-b border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {getCategoryLabel(category)}
                  </h4>
                </div>
                {categoryFonts.map((font) => (
                  <button
                    key={font.family}
                    type="button"
                    onClick={() => handleFontSelect(font.family)}
                    className={`w-full px-2 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors ${
                      value === font.family ? 'bg-green-50 border-green-200' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span 
                        className="text-lg text-gray-700"
                        style={{ fontFamily: font.family }}
                      >
                        Aa
                      </span>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{font.family}</div>
                        <div 
                          className="text-xs text-gray-600"
                          style={{ fontFamily: font.family }}
                        >
                          {getFontPreviewText(category)}
                        </div>
                      </div>
                      {value === font.family && (
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FontSelector;